import * as React from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import {
  HighlightRemixPanel,
  type HighlightRemixSource,
} from "@/src/components/HighlightRemixPanel.tsx";
import { useProjectContext } from "@/src/context/ProjectContext.tsx";
import type { StudioProduct } from "@/src/types.ts";

interface HighlightsSearch {
  /** Product to preselect (`?product=<productId>`, set by the Studio dropdown). */
  product?: string;
}

function uploadedProductSource(
  product: StudioProduct
): HighlightRemixSource | null {
  const image = product.originalImage;
  const displayUrl = image?.thumbnailUrl || image?.fullUrl || "";
  if (!image?.storagePath || !displayUrl) return null;

  return {
    product,
    displayUrl,
    content: {
      id: `source-${product.id}`,
      title: (product.info || image.name || "").trim() || undefined,
      image: displayUrl,
      text: null,
      video: null,
      storagePath: image.storagePath,
      thumbnailUrl: image.thumbnailUrl,
      fullUrl: image.fullUrl,
      filename: image.name,
      mimeType: image.mimeType,
      hasAlpha: image.hasAlpha,
      dimensions:
        image.width && image.height
          ? { width: image.width, height: image.height }
          : undefined,
    },
  };
}

const HighlightsStudio: React.FC = () => {
  const { t } = useTranslation();
  const {
    studioProducts,
    isLoadingProducts,
    productsLoadFailed,
    retryProductsLoad,
  } = useProjectContext();
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const sources = React.useMemo(
    () =>
      studioProducts.flatMap((product) => {
        const uploaded = uploadedProductSource(product);
        const generated = product.generatedContent
          .filter((content) => !content.video)
          .map((content) => ({
            product,
            content,
            displayUrl: content.thumbnailUrl || content.image || "",
          }))
          .filter(
            (source): source is HighlightRemixSource => source.displayUrl !== ""
          );
        return uploaded ? [uploaded, ...generated] : generated;
      }),
    [studioProducts]
  );

  // Once the panel has applied the preselection the param has done its job;
  // clearing it keeps a reload or a back-navigation from re-selecting.
  const handleInitialProductConsumed = React.useCallback(() => {
    navigate({
      to: "/projects/$projectId/highlights",
      params: { projectId },
      search: {},
      replace: true,
    });
  }, [navigate, projectId]);

  // An empty collection during loading is not an empty project. Mounting
  // the upload panel here would let the initial snapshot replace new uploads.
  if (isLoadingProducts) {
    return (
      <div role="status" className="p-6 text-sm text-gray-500">
        {t("common.loading")}
      </div>
    );
  }
  if (productsLoadFailed) {
    return (
      <div role="alert" className="flex items-center gap-4 p-6">
        <p className="text-sm text-gray-700">{t("studio.errorLoadProducts")}</p>
        <button
          type="button"
          onClick={retryProductsLoad}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold hover:bg-gray-50"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  return (
    <HighlightRemixPanel
      projectId={projectId}
      sources={sources}
      initialProductId={search.product}
      onInitialProductConsumed={handleInitialProductConsumed}
    />
  );
};

export const Route = createFileRoute("/projects/$projectId/highlights")({
  component: HighlightsStudio,
  validateSearch: (search: Record<string, unknown>): HighlightsSearch => ({
    product: typeof search.product === "string" ? search.product : undefined,
  }),
});
